import { useState } from 'react';
import { FileText, Download, ChevronRight, BookOpen, ArrowLeft } from 'lucide-react';
import PortalLayout from '../shared/components/PortalLayout';
import { useAuth, userIsAdmin } from '../context/AuthContext';
import { GUIDES, getGuidesForUser } from './guideContent';
import { AMBER, AMBER_DARK, NAVY, BORDER_COLOR, BG_FIELD, bubbleStyle } from '../styles/tokens';
import client from '../api/client';

export default function GuiasPage() {
  const { user } = useAuth();
  const [selectedGuide, setSelectedGuide] = useState(null);
  const visibleGuides = getGuidesForUser(user);

  async function handleDownload(guide) {
    try {
      const res = await client.get(`/api/guides/${guide.key}`, { responseType: 'arraybuffer' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = guide.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Error al descargar la guía.');
    }
  }

  return (
    <PortalLayout activeModule="guias">
      <div style={{ padding: '28px 32px', maxWidth: 960, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          {selectedGuide && (
            <button
              onClick={() => setSelectedGuide(null)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'none', border: 'none', cursor: 'pointer',
                color: AMBER, fontSize: 13, fontWeight: 600,
                fontFamily: 'Inter, sans-serif', marginBottom: 12, padding: 0,
              }}
            >
              <ArrowLeft size={15} /> Volver a Guías
            </button>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <BookOpen size={26} color={NAVY} />
            <h1 style={{ fontSize: 22, fontWeight: 700, color: NAVY, margin: 0 }}>
              {selectedGuide ? selectedGuide.title : 'Guías de Usuario'}
            </h1>
          </div>
          <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>
            {selectedGuide
              ? selectedGuide.subtitle
              : 'Consulte las guías disponibles para su rol. Puede descargarlas o consultarlas aquí.'}
          </p>
        </div>

        {/* Guide List */}
        {!selectedGuide && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {visibleGuides.map(guide => (
              <div
                key={guide.key}
                style={{
                  ...bubbleStyle,
                  display: 'flex', alignItems: 'center', gap: 18,
                  cursor: 'pointer',
                  transition: 'box-shadow 0.15s, border-color 0.15s',
                }}
                onClick={() => setSelectedGuide(guide)}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = AMBER;
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(217,119,6,0.13), 0 4px 16px rgba(0,0,0,0.04)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = BORDER_COLOR;
                  e.currentTarget.style.boxShadow = bubbleStyle.boxShadow;
                }}
              >
                {/* Icon */}
                <div style={{
                  width: 52, height: 52, borderRadius: 14,
                  background: BG_FIELD,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 26, flexShrink: 0,
                }}>
                  {guide.icon}
                </div>

                {/* Text */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: NAVY, marginBottom: 3 }}>
                    {guide.title}
                  </div>
                  <div style={{ fontSize: 13, color: '#64748b' }}>
                    {guide.subtitle}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  <button
                    onClick={e => { e.stopPropagation(); handleDownload(guide); }}
                    title="Descargar PDF"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 36, height: 36, borderRadius: 8,
                      background: BG_FIELD, border: `1px solid ${BORDER_COLOR}`,
                      cursor: 'pointer', color: NAVY,
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
                    onMouseLeave={e => e.currentTarget.style.background = BG_FIELD}
                  >
                    <Download size={16} />
                  </button>
                  <ChevronRight size={18} color="#94a3b8" />
                </div>
              </div>
            ))}

            {visibleGuides.length === 0 && (
              <div style={{
                ...bubbleStyle, textAlign: 'center', padding: 40,
                color: '#94a3b8', fontSize: 14,
              }}>
                No hay guías disponibles para su rol.
              </div>
            )}
          </div>
        )}

        {/* Guide Detail (Table of Contents) */}
        {selectedGuide && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {/* Download banner */}
            <div style={{
              ...bubbleStyle,
              display: 'flex', alignItems: 'center', gap: 16,
              background: '#fffbeb', borderColor: '#fde68a',
            }}>
              <FileText size={22} color={AMBER} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: NAVY, marginBottom: 2 }}>
                  Descargue esta guía
                </div>
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  Obtenga el documento completo con instrucciones paso a paso e indicadores para capturas de pantalla.
                </div>
              </div>
              <button
                onClick={() => handleDownload(selectedGuide)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: AMBER, color: '#fff', border: 'none',
                  borderRadius: 8, padding: '10px 20px',
                  fontWeight: 600, fontSize: 13, cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = AMBER_DARK}
                onMouseLeave={e => e.currentTarget.style.background = AMBER}
              >
                <Download size={15} />
                Descargar
              </button>
            </div>

            {/* Sections */}
            <div style={bubbleStyle}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: NAVY, marginBottom: 16 }}>
                Contenido de la Guía
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {selectedGuide.sections.map((section, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '12px 14px',
                      borderBottom: i < selectedGuide.sections.length - 1 ? `1px solid ${BORDER_COLOR}` : 'none',
                    }}
                  >
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: BG_FIELD, border: `1px solid ${BORDER_COLOR}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 700, color: AMBER,
                      flexShrink: 0,
                    }}>
                      {i + 1}
                    </div>
                    <span style={{ fontSize: 14, color: NAVY }}>
                      {section}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Role info */}
            <div style={{
              ...bubbleStyle,
              background: BG_FIELD,
              fontSize: 12, color: '#64748b',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <BookOpen size={14} />
              <span>
                Esta guía aplica para: <strong style={{ color: NAVY }}>
                  {selectedGuide.roles.map(r => ({
                    employee: 'Chofer',
                    supervisor: 'Supervisor(a)',
                    engineer: 'Ingeniero(a)',
                    manager: 'Gerente',
                    reviewer: 'Revisión Final',
                    accountant: 'Contabilidad',
                    admin: 'Administrador',
                  }[r] || r)).join(', ')}
                </strong>
              </span>
            </div>
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
